/**
 * Tax-rate lineage — the TS twin of the SQL `tax_rate_in_force(rate_id, date)`.
 *
 * When a rate changes (NL 21% → 23%), the old org row gets an `effective_to`
 * and a successor row opens the day after; the successor's template carries
 * `tax_rate_templates.supersedes_template_id` → the old template. Anything
 * that stores a rate id (an account's default rate, a recurring template's
 * line) keeps naming the old row, so the point of use walks the lineage to
 * the member in force on the document date instead of posting a rate that
 * has ended.
 *
 * The lineage, exactly as the SQL walks it: rows of the SAME direction whose
 * `template_id` is connected to the rate's template through
 * `supersedes_template_id` (both ways, transitively) AND — for a combined
 * state + local row — whose `local_template_id` is connected to the rate's
 * local template through the local template's `supersedes_template_id`
 * (`local_supersedes_template_id` here); a row without a local template only
 * matches rows without one. So a combined row may move on both sides at once
 * (the state rate and the town's rate changing on the same day), and a
 * Vermont combined row never hops to another town or to the state-only row.
 * A custom rate (no `template_id`) has no lineage: itself when in force, else
 * null. Null also means "no member covers the day" (a removed rate), an
 * unknown id, or two members covering the same day — callers never fall back
 * to another rate, and the walk never guesses.
 *
 * The walk only sees the rows it is given (the org's rate list, which holds
 * every lineage member the rate sync copied), so `rates` must include the
 * expired and future-dated rows, not just the active-today ones.
 */
import { isRateEffectiveOn } from "./taxRateWindow.js";
/**
 * The template ids connected to `start` by the supersedes edges the rows
 * carry (`template → supersedes`), walked both ways.
 */
function connectedTemplates(rows, start, templateOf, supersedesOf) {
    const neighbours = new Map();
    const link = (a, b) => {
        if (!neighbours.has(a))
            neighbours.set(a, new Set());
        neighbours.get(a).add(b);
    };
    for (const row of rows) {
        const template = templateOf(row);
        const supersedes = supersedesOf(row);
        if (template && supersedes) {
            link(template, supersedes);
            link(supersedes, template);
        }
    }
    const seen = new Set([start]);
    const queue = [start];
    while (queue.length > 0) {
        const current = queue.shift();
        for (const next of neighbours.get(current) ?? []) {
            if (!seen.has(next)) {
                seen.add(next);
                queue.push(next);
            }
        }
    }
    return seen;
}
/**
 * The member of `rateId`'s lineage in force on `ymd` (`YYYY-MM-DD`), or null.
 * Returns the rate itself when its own window covers the day.
 */
export function rateInForce(rates, rateId, ymd) {
    const rows = rates ?? [];
    const rate = rateId ? rows.find((row) => row.id === rateId) : undefined;
    if (!rate)
        return null;
    if (isRateEffectiveOn(rate, ymd))
        return rate;
    const template = rate.template_id ?? null;
    // A custom rate has no lineage.
    if (!template)
        return null;
    const local = rate.local_template_id ?? null;
    const stateLineage = connectedTemplates(rows, template, (row) => row.template_id, (row) => row.supersedes_template_id);
    const localLineage = local
        ? connectedTemplates(rows, local, (row) => row.local_template_id, (row) => row.local_supersedes_template_id)
        : null;
    const direction = rate.direction ?? null;
    const members = rows.filter((row) => (row.direction ?? null) === direction &&
        !!row.template_id &&
        stateLineage.has(row.template_id) &&
        (localLineage === null
            ? !row.local_template_id
            : !!row.local_template_id && localLineage.has(row.local_template_id)));
    const inForce = members.filter((row) => isRateEffectiveOn(row, ymd));
    // Lineage windows never overlap; two members covering one day is broken
    // data, and the rule is never to guess.
    return inForce.length === 1 ? inForce[0] : null;
}
//# sourceMappingURL=taxRateLineage.js.map